/**
 * One folder row in the mailbox sidebar: a select button plus a "⋯" actions
 * menu (mark all read/unread, empty, and — for custom folders — rename/delete).
 * The menu reveals on hover/focus on the desktop sidebar; on touch (`alwaysShowMenu`)
 * it's always visible. Purely presentational — the page owns the mutations and
 * confirmations and passes them in (an omitted callback hides that item). When
 * every menu callback is omitted the trigger disappears entirely — that's how
 * the unified "All accounts" sidebar renders navigate-and-drop-only rows.
 *
 * The row is also a drop target for messages dragged from the list: when the
 * page marks `canAcceptDrop` (the dragged message belongs to this folder's
 * account and isn't already here), a drop calls `onDropMessage`.
 */

import { useState } from 'react';
import {
  Mail,
  MailOpen,
  MoreVertical,
  Pencil,
  Trash2,
  ChevronRight,
  ChevronDown,
  FolderPlus,
} from 'lucide-react';
import type { EmailFolderDto } from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

/** Indent per nesting level, in px — applied to the leading chevron/spacer. */
const FOLDER_INDENT_PX = 14;

export interface FolderSidebarItemProps {
  folder: EmailFolderDto;
  isActive: boolean;
  /** Always show the actions trigger (touch — no hover). */
  alwaysShowMenu?: boolean;
  /** Nesting depth in the folder tree (0 = top level); indents the row. */
  depth?: number;
  /** True when this folder has child folders — shows an expand/collapse chevron. */
  hasChildren?: boolean;
  /** Whether this folder's subtree is currently shown (only meaningful with children). */
  isExpanded?: boolean;
  /** Toggle this folder's subtree open/closed. */
  onToggleExpand?: () => void;
  onSelect: () => void;
  onMarkRead?: () => void;
  onMarkUnread?: () => void;
  /** Offered for folders where emptying is a normal action (Trash / Spam). */
  onEmpty?: () => void;
  /** Custom folders only — system folders can't be renamed/deleted. */
  onRename?: () => void;
  onDelete?: () => void;
  /** Custom folders only — create a child folder nested under this one. */
  onCreateChild?: () => void;
  /**
   * True while a message is being dragged that this folder can receive. Enables
   * the drop highlight and lets `onDropMessage` fire; false/undefined makes the
   * row inert to drags (e.g. a cross-account or same-folder drag).
   */
  canAcceptDrop?: boolean;
  /**
   * Called when a droppable message is released over this folder. `copy` is true
   * when a copy-modifier (Ctrl/⌘) was held — a cross-account copy rather than a
   * move (Thunderbird-style).
   */
  onDropMessage?: (opts: { copy: boolean }) => void;
}

export function FolderSidebarItem({
  folder,
  isActive,
  alwaysShowMenu,
  depth = 0,
  hasChildren,
  isExpanded,
  onToggleExpand,
  onSelect,
  onMarkRead,
  onMarkUnread,
  onEmpty,
  onRename,
  onDelete,
  onCreateChild,
  canAcceptDrop,
  onDropMessage,
}: FolderSidebarItemProps) {
  const count = folder.messageCount ?? 0;
  const unread = folder.unreadCount ?? 0;
  const hasMenu = !!(
    onMarkRead ||
    onMarkUnread ||
    onEmpty ||
    onRename ||
    onDelete ||
    onCreateChild
  );
  // Local highlight while a droppable message hovers this row. Kept here (not in
  // the page) so only the row under the cursor lights up.
  const [isDropOver, setIsDropOver] = useState(false);
  const droppable = !!onDropMessage;
  return (
    <div
      data-testid={`folder-row-${folder.folderId}`}
      onDragOver={
        droppable
          ? (e) => {
              if (!canAcceptDrop) return;
              e.preventDefault(); // allow the drop
              // Reflect Ctrl/⌘ as a copy cursor, matching the drop behavior below.
              e.dataTransfer.dropEffect = e.ctrlKey || e.metaKey ? 'copy' : 'move';
              setIsDropOver(true);
            }
          : undefined
      }
      onDragLeave={droppable ? () => setIsDropOver(false) : undefined}
      onDrop={
        droppable
          ? (e) => {
              setIsDropOver(false);
              if (!canAcceptDrop) return;
              e.preventDefault();
              onDropMessage?.({ copy: e.ctrlKey || e.metaKey });
            }
          : undefined
      }
      className={`group/folder flex items-center rounded text-sm ${
        isDropOver && canAcceptDrop
          ? 'ring-2 ring-primary ring-inset bg-primary/10'
          : isActive
            ? 'bg-accent font-medium'
            : 'hover:bg-accent/50'
      }`}
    >
      {/* Leading indent + expand/collapse chevron (or an aligning spacer). The
          chevron is its own button so toggling never selects the folder. */}
      <span
        className="flex shrink-0 items-center"
        style={depth > 0 ? { paddingLeft: depth * FOLDER_INDENT_PX } : undefined}
      >
        {hasChildren ? (
          <button
            type="button"
            aria-label={isExpanded ? `Collapse ${folder.name}` : `Expand ${folder.name}`}
            className="flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:bg-accent"
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand?.();
            }}
          >
            {isExpanded ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
          </button>
        ) : (
          <span className="w-5" aria-hidden="true" />
        )}
      </span>
      <button
        className="flex min-w-0 flex-1 items-center gap-2 py-1.5 pr-2 text-left"
        onClick={onSelect}
      >
        <Mail className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <span className={`truncate ${unread > 0 ? 'font-semibold' : ''}`}>{folder.name}</span>
        {(count > 0 || unread > 0) && (
          <span className="ml-auto shrink-0 text-[11px] tabular-nums text-muted-foreground">
            {count.toLocaleString()}
            {/* Unread, bold + accented, in parentheses — the at-a-glance "needs
                attention" cue alongside the total. Omitted when all-read. */}
            {unread > 0 && (
              <span className="ml-1 font-semibold text-primary">({unread.toLocaleString()})</span>
            )}
          </span>
        )}
      </button>
      {hasMenu && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className={`mr-0.5 h-6 w-6 shrink-0 p-0 ${
                alwaysShowMenu
                  ? ''
                  : 'opacity-0 focus:opacity-100 group-hover/folder:opacity-100 data-[state=open]:opacity-100'
              }`}
              aria-label={`Actions for ${folder.name}`}
              onClick={(e) => e.stopPropagation()}
            >
              <MoreVertical className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            {onMarkRead && (
              <DropdownMenuItem onClick={onMarkRead}>
                <MailOpen className="mr-2 h-3.5 w-3.5" /> Mark all as read
              </DropdownMenuItem>
            )}
            {onMarkUnread && (
              <DropdownMenuItem onClick={onMarkUnread}>
                <Mail className="mr-2 h-3.5 w-3.5" /> Mark all as unread
              </DropdownMenuItem>
            )}
            {onEmpty && (
              <DropdownMenuItem
                onClick={onEmpty}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="mr-2 h-3.5 w-3.5" /> Empty folder
              </DropdownMenuItem>
            )}
            {(onRename || onDelete || onCreateChild) && <DropdownMenuSeparator />}
            {onCreateChild && (
              <DropdownMenuItem onClick={onCreateChild}>
                <FolderPlus className="mr-2 h-3.5 w-3.5" /> New subfolder…
              </DropdownMenuItem>
            )}
            {onRename && (
              <DropdownMenuItem onClick={onRename}>
                <Pencil className="mr-2 h-3.5 w-3.5" /> Rename…
              </DropdownMenuItem>
            )}
            {onDelete && (
              <DropdownMenuItem
                onClick={onDelete}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="mr-2 h-3.5 w-3.5" /> Delete folder…
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
